import { Component, OnInit, inject, AfterViewChecked, PLATFORM_ID, Inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Title, Meta, DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ressources } from '../../../data/ressources';
import { Ressource } from '@models/ressource';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';

@Component({
  selector: 'app-ressource-detail',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './ressource-detail.component.html',
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        query('.animate-fade-in-up', [
          style({ opacity: 0, transform: 'translateY(20px)' }),
          stagger('100ms', [
            animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true }),
        query('.animate-fade-in', [
          style({ opacity: 0 }),
          animate('500ms ease-out', style({ opacity: 1 }))
        ], { optional: true }),
        query('.animate-fade-in-down', [
          style({ opacity: 0, transform: 'translateY(-20px)' }),
          animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
        ], { optional: true })
      ])
    ])
  ]
})
export class RessourceDetailComponent implements OnInit, AfterViewChecked {
  article: Ressource | undefined;
  safeContent: SafeHtml | undefined;
  private hasHighlighted = false;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private sanitizer = inject(DomSanitizer);
  private platformId = inject(PLATFORM_ID);

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      this.article = ressources.find((r: Ressource) => r.id === id);

      if (!this.article) {
        this.router.navigate(['/ressources'], { replaceUrl: true });
        return;
      }

      this.updateSEO();
      if (this.article.content) {
        this.safeContent = this.sanitizer.bypassSecurityTrustHtml(this.article.content);
      }
      // Reset flag when article changes
      this.hasHighlighted = false;
    });
  }

  async ngAfterViewChecked() {
    // Only highlight once per article, and only in browser (SSR-safe)
    if (isPlatformBrowser(this.platformId) && !this.hasHighlighted && this.article) {
      this.hasHighlighted = true;
      // Lazy load PrismJS
      const Prism = await import('prismjs');
      Prism.default.highlightAll();
    }
  }

  updateSEO() {
    if (this.article) {
      const fullUrl = `https://www.mesh-box.fr/ressources/${this.article.id}`;
      this.titleService.setTitle(`${this.article.title} — Mesh Box`);
      this.metaService.updateTag({ name: 'description', content: this.article.excerpt });
      
      this.metaService.updateTag({ property: 'og:title', content: this.article.title });
      this.metaService.updateTag({ property: 'og:description', content: this.article.excerpt });
      this.metaService.updateTag({ property: 'og:type', content: 'article' });
      this.metaService.updateTag({ property: 'og:url', content: fullUrl });
      this.metaService.updateTag({ property: 'og:image', content: `https://www.mesh-box.fr${this.article.image}` });
      this.metaService.updateTag({ property: 'og:site_name', content: 'Mesh Box' });
      
      this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
      this.metaService.updateTag({ name: 'twitter:title', content: this.article.title });
      this.metaService.updateTag({ name: 'twitter:description', content: this.article.excerpt });
      this.metaService.updateTag({ name: 'twitter:image', content: `https://www.mesh-box.fr${this.article.image}` });
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  }
}
